import React, { useState, useEffect } from "react";
import { Container, Typography, Paper, Grid, Box, CircularProgress, Alert } from "@mui/material";
import { db } from "../../Firebase";
import { doc, getDoc } from "firebase/firestore";

interface TakeSetData {
  setId: string;
  setName: string;
  creator: string;
  description: string;
  images: {
    title?: string;
    rule?: string;
    junbi?: string;
    end?: string;
  };
  problems: Array<{
    problemId: string;
    problemNumber: number;
    images: {
      problem?: string;
      answer?: string;
    };
  }>;
}

const ViewTakeImages: React.FC = () => {
  const [takeSet, setTakeSet] = useState<TakeSetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTakeSet = async () => {
      try {
        const takeSetDoc = await getDoc(doc(db, "v3sets", "take-set"));
        
        if (takeSetDoc.exists()) {
          setTakeSet(takeSetDoc.data() as TakeSetData);
        } else {
          setError("takeセットが見つかりません");
        }
      } catch (err) {
        console.error("Error fetching take set:", err);
        setError("データの取得中にエラーが発生しました");
      } finally {
        setLoading(false);
      }
    };

    fetchTakeSet();
  }, []);

  if (loading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!takeSet) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">takeセットのデータがありません</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        takeセット画像確認
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          セット情報
        </Typography>
        <Box>
          <Typography><strong>セットID:</strong> {takeSet.setId}</Typography>
          <Typography><strong>セット名:</strong> {takeSet.setName}</Typography>
          <Typography><strong>作成者:</strong> {takeSet.creator}</Typography>
          <Typography><strong>説明:</strong> {takeSet.description}</Typography>
          <Typography><strong>問題数:</strong> {takeSet.problems.length}</Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          基本画像
        </Typography>
        <Grid container spacing={2}>
          {takeSet.images.title && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>タイトル</Typography>
              <img 
                src={takeSet.images.title} 
                alt="タイトル" 
                style={{ width: "100%", maxWidth: 400, border: "1px solid #ccc" }}
              />
            </Grid>
          )}
          {takeSet.images.rule && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>ルール</Typography>
              <img 
                src={takeSet.images.rule} 
                alt="ルール" 
                style={{ width: "100%", maxWidth: 400, border: "1px solid #ccc" }}
              />
            </Grid>
          )}
          {takeSet.images.junbi && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>準備</Typography>
              <img 
                src={takeSet.images.junbi} 
                alt="準備" 
                style={{ width: "100%", maxWidth: 400, border: "1px solid #ccc" }}
              />
            </Grid>
          )}
          {takeSet.images.end && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>終了</Typography>
              <img 
                src={takeSet.images.end} 
                alt="終了" 
                style={{ width: "100%", maxWidth: 400, border: "1px solid #ccc" }}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          問題画像
        </Typography>
        {takeSet.problems.map((problem) => (
          <Box key={problem.problemId} sx={{ mb: 4, pb: 4, borderBottom: "1px solid #eee" }}>
            <Typography variant="subtitle1" gutterBottom>
              問題 {problem.problemNumber}
            </Typography>
            <Grid container spacing={2}>
              {problem.images.problem && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>問題</Typography>
                  <img 
                    src={problem.images.problem} 
                    alt={`問題 ${problem.problemNumber}`} 
                    style={{ width: "100%", maxWidth: 400, border: "1px solid #ccc" }}
                  />
                </Grid>
              )}
              {problem.images.answer && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>解答</Typography>
                  <img 
                    src={problem.images.answer} 
                    alt={`解答 ${problem.problemNumber}`} 
                    style={{ width: "100%", maxWidth: 400, border: "1px solid #ccc" }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
      </Paper>
    </Container>
  );
};

export default ViewTakeImages;